<template>
  <div class="to-do-content">
    <ul v-if="isGenerating" class="to-do-content__skeleton" data-testid="to-do-generating">
      <li v-for="n in 3" :key="n" class="to-do-content__skeleton-row animate-pulse">
        <div class="to-do-content__skeleton-checkbox bg-zinc-100 dark:bg-zinc-800"></div>
        <div class="to-do-content__skeleton-text bg-zinc-100 dark:bg-zinc-800"></div>
      </li>
    </ul>

    <template v-else>
      <div v-if="isFailed" class="to-do-content__failure" data-testid="to-do-failure">
        <p class="to-do-content__empty-text">Couldn't generate a to-do list.</p>
        <p v-if="isCurrentItemOwner" class="to-do-content__hint" data-testid="to-do-hint">Run the smart action again from AI Actions to try again.</p>
      </div>

      <template v-if="isEmpty && !isFailed">
        <p class="to-do-content__empty-text" data-testid="to-do-empty">No to-dos for this video yet.</p>
      </template>

      <ul v-if="!isEmpty" class="to-do-content__list">
        <li v-for="item in items" :key="item.id" class="to-do-content__row">
          <div class="to-do-content__checkbox">
            <button
              v-if="item.videoTime !== null"
              type="button"
              class="to-do-content__timestamp"
              :data-testid="`to-do-timestamp-${item.id}`"
              @click="seekTo(item)"
            >
              {{ formatDuration(item.videoTime) }}
            </button>
            <FormKit
              type="checkbox"
              :model-value="item.status === 'done'"
              :label="item.text"
              :disabled="!isCurrentItemOwner"
              :data-testid="`to-do-item-${item.id}`"
              @input="(checked: boolean) => handleToggle(item, checked)"
            />
          </div>

          <div v-if="isCurrentItemOwner" class="to-do-content__actions">
            <button
              type="button"
              class="to-do-content__action-label"
              :data-testid="`to-do-actions-trigger-${item.id}`"
              @click="handleAction(currentAction(item), item)"
            >
              {{ actionLabel(currentAction(item)) }}
            </button>

            <ContextMenuWrapper placement="bottom-end" :test-id="`to-do-actions-${item.id}`">
              <button type="button" class="to-do-content__action-arrow" :data-testid="`to-do-actions-arrow-${item.id}`" aria-label="Choose an action">
                <!-- chevronDown bakes in its own circular background (meant for
                     standalone icon-buttons); chevronUp has none, so it's
                     rotated instead of fighting that circle with CSS. -->
                <Icon name="general.chevronUp" size="xs" class="rotate-180" />
              </button>
              <template #menu>
                <MenuLink
                  v-for="action in orderedActions(item)"
                  :key="action"
                  :title="actionLabel(action)"
                  :data-testid="`to-do-action-${action}-${item.id}`"
                  @click="handleAction(action, item)"
                >
                  {{ actionLabel(action) }}
                </MenuLink>
              </template>
            </ContextMenuWrapper>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon, ContextMenuWrapper, MenuLink } from '@/storybook';
import { useToast } from '@/composables/useToast';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useIntegrationStore } from '@/modules/dashboard/integrations/store/useIntegrationStore';
import { useExportUtils } from '@/modules/viewer/composables/useExportUtils';
import { useUser } from '@/modules/viewer/composables/useUser';
import { formatDuration } from '@/helpers/formatDuration';
import { useToDoStore, type ToDoItem } from '@/modules/viewer/store/useToDoStore';
import { AiSmartActionStatus, useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';

type ToDoAction = 'ticket' | 'documentation' | 'email';

const itemStore = useViewerItemStore();
const { id: itemId, displayName, shareLink } = storeToRefs(itemStore);
const toDoStore = useToDoStore();
const { items } = storeToRefs(toDoStore);
const smartActionsStore = useSmartActionsStore();
const { isActioning } = storeToRefs(smartActionsStore);
const integrationsStore = useIntegrationStore();
const entitlementsStore = useEntitlementsStore();
const { isCurrentItemOwner } = useUser();
const { success, error } = useToast();
const { debug } = useDebug(DEBUG_NAMESPACE.STORE_TO_DO);

const { copyHtmlFragment } = useExportUtils(ref(''), displayName);

// Per-row override once the user picks a non-default action from the
// dropdown - the label button then shows and repeats that choice instead
// of reverting to the item's classified-type default on every render.
const selectedActions = ref<Record<string, ToDoAction>>({});
const currentAction = (item: ToDoItem): ToDoAction => selectedActions.value[item.id] ?? defaultAction(item);

// The "Create ticket" action needs to know whether Jira is actually
// connected - fetch it here rather than assuming the Export tab already did,
// since the owner can land on this tab without ever opening Export.
onMounted(() => {
  if (isCurrentItemOwner.value) {
    integrationsStore.fetchIntegrations();
  }
});

// The list is produced by its own run after the smart action, so it is still
// coming while either one is in flight.
const isGenerating = computed(() => isActioning.value || toDoStore.isGenerating);
const isEmpty = computed(() => items.value.length === 0);
const isFailed = computed(() => smartActionsStore.actionStatus === AiSmartActionStatus.FAILED || toDoStore.hasGenerationFailed);

const handleToggle = (item: ToDoItem, checked: boolean) => {
  if (!itemId.value || !isCurrentItemOwner.value) {
    return;
  }
  toDoStore.setStatus(itemId.value, item.id, checked ? 'done' : 'open');
};

const seekTo = (item: ToDoItem) => {
  if (item.videoTime === null) {
    return;
  }
  itemStore.setVideoTime(item.videoTime);
};

// The item's AI-classified type only suggests which action leads the
// dropdown - all three stay available on every row.
const defaultAction = (item: ToDoItem): ToDoAction => {
  if (item.suggestedAction === 'documentation') {
    return 'documentation';
  }
  if (item.suggestedAction === 'email') {
    return 'email';
  }
  return 'ticket';
};

const orderedActions = (item: ToDoItem): ToDoAction[] => {
  const suggested = currentAction(item);
  return [suggested, ...(['ticket', 'documentation', 'email'] as ToDoAction[]).filter(action => action !== suggested)];
};

const actionLabel = (action: ToDoAction) =>
  ({
    ticket: 'Create ticket',
    documentation: 'Copy to clipboard',
    email: 'Send via email',
  })[action];

const handleAction = async (action: ToDoAction, item: ToDoItem) => {
  selectedActions.value[item.id] = action;

  try {
    if (action === 'ticket') {
      await handleCreateTicket(item);
      return;
    }
    if (action === 'email') {
      handleSendEmail(item);
      return;
    }
    await copyHtmlFragment(item.text, { linkToSmartAction: false });
  } catch (actionError) {
    error('Something went wrong completing that action. Please try again.');
    debug(`Failed to complete ${action} for to-do item ${item.id}`, actionError);
  }
};

// Opens the viewer's default mail client with the row pre-filled - there's no
// backend email integration to send on the user's behalf.
const handleSendEmail = (item: ToDoItem) => {
  const body = shareLink.value ? `${item.text}\n\n${shareLink.value}` : item.text;
  window.location.href = `mailto:?subject=${encodeURIComponent(item.text)}&body=${encodeURIComponent(body)}`;
};

const handleCreateTicket = async (item: ToDoItem) => {
  const canAccess = entitlementsStore.can('premiumIntegrations');
  if (!canAccess) {
    error('This feature requires an upgrade. Please upgrade to continue.');
    return;
  }

  const jira = integrationsStore.integrations.find(integration => integration.name.toLowerCase() === 'jira');
  if (!jira?.enabled) {
    error('Jira is not enabled. Please enable it in your integrations settings.');
    return;
  }

  await integrationsStore.sendTo('jira', item.id);
  success('Your request has been queued. Please check Jira dashboard for updates.');
};
</script>

<style scoped>
.to-do-content {
  @apply flex flex-col gap-3;
}

.to-do-content__empty-text {
  @apply text-sm text-zinc-600 dark:text-zinc-300;
}

.to-do-content__failure {
  @apply flex flex-col gap-1;
}

.to-do-content__hint {
  @apply text-xs text-zinc-500 dark:text-zinc-400;
}

.to-do-content__skeleton {
  @apply flex flex-col gap-3;
}

.to-do-content__skeleton-row {
  @apply flex items-center gap-3;
}

.to-do-content__skeleton-checkbox {
  @apply h-4 w-4 shrink-0 rounded;
}

.to-do-content__skeleton-text {
  @apply h-4 w-2/3 rounded;
}

.to-do-content__list {
  @apply flex flex-col;
}

.to-do-content__row {
  @apply flex items-center justify-between gap-3 py-2 border-b border-zinc-100 dark:border-zinc-700 last:border-b-0;
}

.to-do-content__checkbox {
  @apply flex min-w-0 flex-1 flex-col items-start gap-1;
}

.to-do-content__timestamp {
  @apply text-xs font-medium text-zinc-500 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400;
}

.to-do-content__actions {
  @apply flex items-center shrink-0 whitespace-nowrap rounded-lg bg-zinc-100 dark:bg-zinc-700 overflow-hidden;
}

.to-do-content__action-label {
  @apply px-3 py-1.5 text-xs font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-600 outline-none focus-visible:ring focus-visible:ring-blue-500/50;
}

.to-do-content__action-arrow {
  @apply flex items-center px-2 py-1.5 border-l border-zinc-200 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-600 outline-none focus-visible:ring focus-visible:ring-blue-500/50;
}
</style>
